import React, { useState, useEffect } from 'react';
import { Brain, TrendingUp, DollarSign, Users, Calendar, Download, RefreshCw, BarChart3 } from 'lucide-react';
import { storage } from '../services/storage';
import { AIUsageStats, AIUsage, AIUsageType } from '../types';
import { useModal } from './ui/ModalProvider';

export const AIConsumptionPanel: React.FC = () => {
    const { toast } = useModal();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState<AIUsageStats[]>([]);
    const [detailedUsage, setDetailedUsage] = useState<AIUsage[]>([]);
    const [startDate, setStartDate] = useState<string>(() => {
        const date = new Date();
        date.setMonth(date.getMonth() - 1);
        return date.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState<string>(() => {
        return new Date().toISOString().split('T')[0];
    });
    const [showDetails, setShowDetails] = useState(false);

    useEffect(() => {
        loadData();
    }, [startDate, endDate]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [statsData, usageData] = await Promise.all([
                storage.getAIUsageStats(startDate, endDate),
                storage.getAIUsageList(startDate, endDate)
            ]);
            setStats(statsData);
            setDetailedUsage(usageData);
        } catch (e) {
            console.error('Error loading AI consumption data:', e);
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 4,
            maximumFractionDigits: 6
        }).format(amount);
    };

    const formatNumber = (num: number) => {
        return new Intl.NumberFormat('fr-FR').format(num);
    };

    const getUsageTypeLabel = (type: AIUsageType): string => {
        switch (type) {
            case 'ESTIMATE_PROPOSAL':
                return 'Estimation Proposition';
            case 'GENERATE_PROPERTY_NAME':
                return 'Génération Nom Bien';
            case 'PARSE_LEBONCOIN':
                return 'Parsing Leboncoin';
            default:
                return type;
        }
    };

    const totalTokens = stats.reduce((sum, s) => sum + s.totalTokens, 0);
    const totalCost = stats.reduce((sum, s) => sum + s.totalCostUsd, 0);
    const totalUsageCount = stats.reduce((sum, s) => sum + s.usageCount, 0);

    if (loading) {
        return (
            <div className="p-8 text-center text-slate-500">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                Chargement des données de consommation IA...
            </div>
        );
    }

    return (
        <div className="p-8 h-full overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-2 flex items-center">
                        <Brain className="w-6 h-6 mr-2 text-slate-700" />
                        Consommation IA
                    </h2>
                    <p className="text-slate-500">Suivi des tokens et coûts par utilisateur</p>
                </div>
                <button
                    onClick={loadData}
                    className="flex items-center px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-medium text-sm"
                >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Actualiser
                </button>
            </div>

            {/* Date Range Filter */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
                <div className="flex items-center mb-4">
                    <Calendar className="w-5 h-5 mr-2 text-slate-500" />
                    <h3 className="font-bold text-slate-900">Période</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-1">Date de début</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 focus:ring-amber-500 focus:border-amber-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-1">Date de fin</label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 focus:ring-amber-500 focus:border-amber-500"
                        />
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                            <BarChart3 className="w-6 h-6 text-blue-600" />
                        </div>
                    </div>
                    <h3 className="text-sm font-bold text-slate-500 uppercase mb-1">Total Tokens</h3>
                    <p className="text-2xl font-bold text-slate-900">{formatNumber(totalTokens)}</p>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                            <DollarSign className="w-6 h-6 text-green-600" />
                        </div>
                    </div>
                    <h3 className="text-sm font-bold text-slate-500 uppercase mb-1">Coût Total</h3>
                    <p className="text-2xl font-bold text-slate-900">{formatCurrency(totalCost)}</p>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                            <TrendingUp className="w-6 h-6 text-amber-600" />
                        </div>
                    </div>
                    <h3 className="text-sm font-bold text-slate-500 uppercase mb-1">Utilisations</h3>
                    <p className="text-2xl font-bold text-slate-900">{formatNumber(totalUsageCount)}</p>
                </div>
            </div>

            {/* User Statistics Table */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mb-6">
                <div className="p-6 border-b border-slate-200 flex justify-between items-center">
                    <h3 className="text-lg font-bold text-slate-900 flex items-center">
                        <Users className="w-5 h-5 mr-2 text-slate-500" />
                        Statistiques par Utilisateur
                    </h3>
                    <button
                        onClick={() => setShowDetails(!showDetails)}
                        className="text-sm text-slate-600 hover:text-slate-900 font-medium"
                    >
                        {showDetails ? 'Masquer détails' : 'Afficher détails'}
                    </button>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                            <tr>
                                <th className="p-4">Utilisateur</th>
                                <th className="p-4 text-right">Tokens</th>
                                <th className="p-4 text-right">Coût (USD)</th>
                                <th className="p-4 text-right">Utilisations</th>
                                <th className="p-4">Répartition</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {stats.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="p-8 text-center text-slate-400 italic">
                                        Aucune donnée pour cette période
                                    </td>
                                </tr>
                            ) : (
                                stats.map((stat) => (
                                    <tr key={stat.userId} className="hover:bg-slate-50">
                                        <td className="p-4">
                                            <div className="font-bold text-slate-900">{stat.userName}</div>
                                            <div className="text-xs text-slate-500">{stat.userEmail}</div>
                                        </td>
                                        <td className="p-4 text-right font-medium text-slate-900">
                                            {formatNumber(stat.totalTokens)}
                                        </td>
                                        <td className="p-4 text-right font-medium text-slate-900">
                                            {formatCurrency(stat.totalCostUsd)}
                                        </td>
                                        <td className="p-4 text-right font-medium text-slate-900">
                                            {formatNumber(stat.usageCount)}
                                        </td>
                                        <td className="p-4">
                                            <div className="flex flex-col gap-1 text-xs">
                                                {stat.usageByType.ESTIMATE_PROPOSAL > 0 && (
                                                    <div className="flex items-center">
                                                        <span className="w-2 h-2 rounded-full bg-blue-500 mr-2"></span>
                                                        Estimation: {stat.usageByType.ESTIMATE_PROPOSAL}
                                                    </div>
                                                )}
                                                {stat.usageByType.GENERATE_PROPERTY_NAME > 0 && (
                                                    <div className="flex items-center">
                                                        <span className="w-2 h-2 rounded-full bg-green-500 mr-2"></span>
                                                        Nom bien: {stat.usageByType.GENERATE_PROPERTY_NAME}
                                                    </div>
                                                )}
                                                {stat.usageByType.PARSE_LEBONCOIN > 0 && (
                                                    <div className="flex items-center">
                                                        <span className="w-2 h-2 rounded-full bg-purple-500 mr-2"></span>
                                                        Parsing LBC: {stat.usageByType.PARSE_LEBONCOIN}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Detailed Usage List */}
            {showDetails && (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="p-6 border-b border-slate-200">
                        <h3 className="text-lg font-bold text-slate-900">Détail des Utilisations</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                                <tr>
                                    <th className="p-4">Date</th>
                                    <th className="p-4">Type</th>
                                    <th className="p-4">Modèle</th>
                                    <th className="p-4 text-right">Input Tokens</th>
                                    <th className="p-4 text-right">Output Tokens</th>
                                    <th className="p-4 text-right">Total Tokens</th>
                                    <th className="p-4 text-right">Coût (USD)</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {detailedUsage.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="p-8 text-center text-slate-400 italic">
                                            Aucune utilisation enregistrée
                                        </td>
                                    </tr>
                                ) : (
                                    detailedUsage.map((usage) => (
                                        <tr key={usage.id} className="hover:bg-slate-50">
                                            <td className="p-4 text-sm text-slate-600">
                                                {new Date(usage.createdAt).toLocaleDateString('fr-FR', {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </td>
                                            <td className="p-4">
                                                <span className="px-2 py-1 rounded text-xs font-medium bg-slate-100 text-slate-700">
                                                    {getUsageTypeLabel(usage.usageType)}
                                                </span>
                                            </td>
                                            <td className="p-4 text-sm text-slate-600">{usage.model}</td>
                                            <td className="p-4 text-right text-sm text-slate-600">
                                                {formatNumber(usage.inputTokens)}
                                            </td>
                                            <td className="p-4 text-right text-sm text-slate-600">
                                                {formatNumber(usage.outputTokens)}
                                            </td>
                                            <td className="p-4 text-right font-medium text-slate-900">
                                                {formatNumber(usage.totalTokens)}
                                            </td>
                                            <td className="p-4 text-right font-medium text-slate-900">
                                                {formatCurrency(usage.costUsd)}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};
